/**
 * ai-agent.reducer.ts — NgRx reducer and selectors for the generic AI agent state.
 */
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';

import { AiAgentSession } from '../interfaces/ai-agent-session.interface';
import { AiAgentStatus } from '../interfaces/ai-agent-status.enum';
import { AiJobProgressEvent } from '../interfaces/ai-agent-progress-event.interface';
import { AiBackendError } from '../interfaces/ai-backend-error';
import * as AiAgentActions from '../actions/ai-agent.actions';

// ── State Shape ───────────────────────────────────────────────────────

export interface AiAgentState {
    activeSessionId: string | null;
    session: AiAgentSession | null;
    progressEvents: AiJobProgressEvent[];
    isSubmitting: boolean;
    /** Typed backend error — null when there is no error. */
    error: AiBackendError | null;
    socketConnected: boolean;
}

const initialState: AiAgentState = {
    activeSessionId: null,
    session: null,
    progressEvents: [],
    isSubmitting: false,
    error: null,
    socketConnected: false,
};

// ── Feature Key ───────────────────────────────────────────────────────

export const AI_AGENT_FEATURE_KEY = 'aiAgent';

// ── Reducer ───────────────────────────────────────────────────────────

export const aiAgentReducer = createReducer(
    initialState,

    // ── Submit Job ──
    on(AiAgentActions.submitJob, (state) => ({
        ...state,
        isSubmitting: true,
        error: null,
        progressEvents: [],
        session: null,
    })),

    on(AiAgentActions.submitJobSuccess, (state, { sessionId }) => ({
        ...state,
        activeSessionId: sessionId,
        isSubmitting: false,
    })),

    on(AiAgentActions.submitJobFailed, (state, { error }) => ({
        ...state,
        isSubmitting: false,
        error,
    })),

    // ── WebSocket Events ──
    on(AiAgentActions.jobProgressReceived, (state, { event }) => ({
        ...state,
        progressEvents: [...state.progressEvents, event],
    })),

    on(AiAgentActions.jobCompletedReceived, (state, { event }) => ({
        ...state,
        session: state.session
            ? { ...state.session, status: event.status as AiAgentStatus, errorMessage: event.errorMessage ?? null }
            : null,
        error: event.status === 'FAILED'
            ? AiBackendError.fromCompletedEvent(event)
            : state.error,
    })),

    // ── Session Polling ──
    on(AiAgentActions.pollSessionSuccess, (state, { session }) => ({
        ...state,
        session,
    })),

    on(AiAgentActions.pollSessionFailed, (state, { error }) => ({
        ...state,
        error,
    })),

    // ── Chat ──
    on(AiAgentActions.sendChat, (state, { message }) => ({
        ...state,
        progressEvents: [],   // Clear old events so only chat progress accumulates
        session: state.session
            ? {
                ...state.session,
                chatHistory: [
                    ...state.session.chatHistory,
                    { role: 'user' as const, content: message, timestamp: new Date().toISOString() },
                ],
            }
            : null,
    })),

    on(AiAgentActions.sendChatSuccess, (state, { sessionId, reply, replyKey, replyParams, output }) => ({
        ...state,
        session: state.session
            ? {
                ...state.session,
                chatHistory: [
                    ...state.session.chatHistory,
                    {
                        role: 'agent' as const,
                        content: reply,
                        ...(replyKey ? { contentKey: replyKey } : {}),
                        ...(replyParams ? { contentParams: replyParams } : {}),
                        timestamp: new Date().toISOString(),
                    },
                ],
                // Update session output if the chat response included new output
                ...(output != null ? { output } : {}),
            }
            : null,
    })),

    on(AiAgentActions.sendChatFailed, (state, { error }) => ({
        ...state,
        error,
    })),

    // ── Socket ──
    on(AiAgentActions.connectSocket, (state) => ({
        ...state,
        socketConnected: false, // optimistic: will be flipped to true via socketConnectedChanged
    })),

    on(AiAgentActions.socketConnectedChanged, (state, { connected }) => ({
        ...state,
        socketConnected: connected,
    })),

    on(AiAgentActions.disconnectSocket, (state) => ({
        ...state,
        socketConnected: false,
    })),

    // ── Reset ──
    on(AiAgentActions.resetSession, () => ({ ...initialState })),
);

// ── Feature ───────────────────────────────────────────────────────────

export const aiAgentFeature = createFeature({
    name: AI_AGENT_FEATURE_KEY,
    reducer: aiAgentReducer,
});

// ── Selectors ─────────────────────────────────────────────────────────

export const {
    selectActiveSessionId,
    selectSession,
    selectProgressEvents,
    selectIsSubmitting,
    selectError,
    selectSocketConnected,
} = aiAgentFeature;

export const selectStatus = createSelector(
    selectSession,
    selectError,
    (session, error) => {
        if (session?.status) return session.status;
        if (error) return AiAgentStatus.FAILED;
        return null;
    },
);

export const selectOutput = createSelector(
    selectSession,
    (session) => session?.output ?? null,
);

export const selectTrajectory = createSelector(
    selectSession,
    (session) => session?.trajectory ?? [],
);

export const selectChatHistory = createSelector(
    selectSession,
    (session) => session?.chatHistory ?? [],
);
